/**
 * Multi-speaker per-colour styles for /auto-captions.
 *
 * Sits between the speaker diarizer (sets `word.speaker` to "A"/"B"/"C"/…)
 * and the ASS builder (reads `word._speakerColor` to inject a `\c` override
 * per word/line). The pipeline calls __acSpeakerColors.apply(state.words)
 * just before each ASS build so that:
 *
 *   – speakers detected by the diarizer get the default palette colour
 *   – any UI overrides (rename, future palette pick) take precedence
 *   – downstream callers (ASSBuilder, editor swatches, JSON export) all
 *     read from a single source of truth: word._speakerColor
 *
 * Storage model:
 *   – `overrides` maps speaker-label → hex colour. Persisted via
 *     window.__toolSettings under the slug 'auto-captions' so a rename
 *     ("A" → "Alice") survives a page reload of the same transcript.
 *
 * Telemetry:
 *   – No raw analytics here; the speakers card UI calls __aeFeature with
 *     event 'speaker_renamed' after a successful rename. We keep the
 *     module pure so it remains trivially unit-testable.
 */
(function(){
    'use strict';

    var SLUG = 'auto-captions';

    /* Resolve a colour for a label, consulting overrides first then the
       ASSBuilder default palette. Kept here AND in ass-builder.js so this
       module is usable even before ass-builder.js loads (defensive: load
       order is deterministic today but the editor swatch UI is rendered
       from this module's `getDistinct`). */
    function getColor(label, overrides){
        if (!label) return null;
        if (overrides && overrides[label]) return overrides[label];
        if (window.ASSBuilder && typeof window.ASSBuilder.getSpeakerColor === 'function') {
            return window.ASSBuilder.getSpeakerColor(label);
        }
        // Last-resort palette (must match ass-builder.js)
        var fallback = ['#38bdf8', '#f472b6', '#22c55e', '#facc15', '#a78bfa', '#fb923c'];
        var idx = String(label).charCodeAt(0) - 65;
        if (idx < 0) idx = 0;
        return fallback[idx % fallback.length];
    }

    /* Walk every word, stamp `_speakerColor` based on `speaker`. Returns
       the number of words that were stamped — used by the test harness and
       by the editor to short-circuit if nothing changed. */
    function apply(words){
        if (!words || !words.length) return 0;
        var overrides = api.overrides;
        var stamped = 0;
        for (var i = 0; i < words.length; i++) {
            var w = words[i];
            if (!w) continue;
            if (w.speaker) {
                w._speakerColor = getColor(w.speaker, overrides);
                stamped++;
            } else if (w._speakerColor) {
                // Word lost its speaker (toggle off, edit) — drop the tint
                // so it doesn't leak into the next ASS build.
                delete w._speakerColor;
            }
        }
        return stamped;
    }

    /* List unique speakers with their colour swatch + word count. Sorted
       alphabetically by label so the UI list is stable across re-runs. */
    function getDistinct(words){
        words = words || (window.__acState && window.__acState.words) || [];
        var counts = {};
        for (var i = 0; i < words.length; i++) {
            var s = words[i] && words[i].speaker;
            if (!s) continue;
            counts[s] = (counts[s] || 0) + 1;
        }
        var out = [];
        var keys = Object.keys(counts).sort();
        for (var k = 0; k < keys.length; k++) {
            out.push({
                speaker: keys[k],
                color: getColor(keys[k], api.overrides),
                count: counts[keys[k]],
            });
        }
        return out;
    }

    /* Rename a speaker label across `words` — used by the UI rename input.
       Empty / whitespace-only / unchanged names are no-ops; renaming to a
       name that already exists merges the two clusters (preserves intent
       when the user notices "A and C are actually the same person").

       Returns:
         { renamed: <number-of-words-updated>, merged: <bool> }
     */
    function renameSpeaker(oldName, newName, words){
        words = words || (window.__acState && window.__acState.words) || [];
        if (!oldName) return { renamed: 0, merged: false };
        var trimmed = String(newName == null ? '' : newName).trim();
        if (!trimmed) return { renamed: 0, merged: false };
        if (trimmed === oldName) return { renamed: 0, merged: false };

        // Detect merge: does any existing word already use the new name?
        var merged = false;
        for (var i = 0; i < words.length; i++) {
            if (words[i] && words[i].speaker === trimmed) { merged = true; break; }
        }

        var renamed = 0;
        for (var j = 0; j < words.length; j++) {
            if (words[j] && words[j].speaker === oldName) {
                words[j].speaker = trimmed;
                renamed++;
            }
        }

        // Carry over the colour override so renaming doesn't reset the swatch
        if (api.overrides[oldName] && !api.overrides[trimmed]) {
            api.overrides[trimmed] = api.overrides[oldName];
        }
        delete api.overrides[oldName];
        persist();

        // Re-stamp _speakerColor so the next ASS build sees the new label
        apply(words);

        return { renamed: renamed, merged: merged };
    }

    /* Programmatic colour override (UI swatch picker, future palette card).
       Stored separately from per-word `_speakerColor` so we can survive a
       transcript rebuild without losing user choices. */
    function setColor(label, hex){
        if (!label || !hex) return;
        api.overrides[label] = String(hex);
        persist();
    }

    function resetColors(){
        api.overrides = {};
        persist();
    }

    function persist(){
        if (window.__toolSettings && typeof window.__toolSettings.save === 'function') {
            try { window.__toolSettings.save(SLUG, 'speakerOverrides', JSON.stringify(api.overrides)); } catch (e) {}
        }
    }

    function loadOverrides(){
        if (!window.__toolSettings || typeof window.__toolSettings.load !== 'function') return {};
        try {
            var raw = window.__toolSettings.load(SLUG, 'speakerOverrides', '');
            if (!raw) return {};
            var parsed = JSON.parse(raw);
            return (parsed && typeof parsed === 'object') ? parsed : {};
        } catch (e) { return {}; }
    }

    var api = {
        overrides: loadOverrides(),
        apply: apply,
        getDistinct: getDistinct,
        renameSpeaker: renameSpeaker,
        setColor: setColor,
        resetColors: resetColors,
        getColor: function(label){ return getColor(label, api.overrides); },
    };

    window.__acSpeakerColors = api;
})();
